// App-side delegate for `@heidi/session/data` (ARCFE-1003). The v1 list
// queries moved; this file keeps the REVIEW-CONTEXT COMPOSITION app-side —
// whether this user may read another member's list is a six-input decision
// (team details, support role, allowlisted review access, the selected review
// member, the current user, the auth token) plus desktop online-readiness and
// the list-version atom. All of it resolves here and crosses as booleans/ids,
// exactly as the legacy hooks computed them.
import { sessionQueryVersionAtom } from '@/atoms/sessionAtoms';
import { selectedReviewMemberAtom } from '@/atoms/teamAtoms';
import { useIsDesktopOnlineReady } from '@/hooks/desktop/useIsDesktopOnlineReady';
import { useCombinedQueriesTeamGeneralInfo } from '@/hooks/query/useCombinedQueriesTeamGeneralInfo';
import { useCanReviewOtherUsersSessionList } from '@/hooks/useCanReviewOtherUsersSessionList';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { getSessionsByTeamMemberId } from '@/pages/api/scribeAPIV2';
import {
  GET_SESSIONS,
  GET_TEAM_MEMBER_SESSIONS,
  SESSIONS_QUERY_RETRY_DELAY,
} from '@heidi/session/contracts';
import {
  useQueryAdminSessionsV1,
  useQueryTeamMemberSessionsV1,
} from '@heidi/session/data';
import { useAtomValue } from 'jotai';
import { useIsReviewingOtherUser, useQueryUser } from './useQueryUser';

export { GET_SESSIONS, GET_TEAM_MEMBER_SESSIONS };

/** The review-context decision, resolved once and shared by both hooks. */
function useSessionListReviewContext() {
  const { teamDetails, isSupportRole } = useCombinedQueriesTeamGeneralInfo();
  const selectedReviewUser = useAtomValue(selectedReviewMemberAtom);
  const hasAllowlistedSessionReviewAccess = useCanReviewOtherUsersSessionList();
  return {
    teamId: teamDetails?.team.id,
    memberId: selectedReviewUser ?? undefined,
    canReviewOtherUsersSessionList: isSupportRole || hasAllowlistedSessionReviewAccess,
  };
}

export function useQuerySessions() {
  const { teamId, memberId, canReviewOtherUsersSessionList } =
    useSessionListReviewContext();
  const teamMemberSessions = useQueryTeamMemberSessions();
  const adminSessions = useQueryAdminSessions();
  const { data: kindeUserId } = useQueryUser((data) => data?.kinde_user_id);

  if (teamId && memberId && memberId !== kindeUserId && canReviewOtherUsersSessionList) {
    return teamMemberSessions;
  }
  return adminSessions;
}

export function useQueryAdminSessions() {
  const { isAuthenticated } = useIsAuthenticated();
  const { data: user } = useQueryUser();
  const sessionQueryVersion = useAtomValue(sessionQueryVersionAtom);
  const isDesktopOnlineReady = useIsDesktopOnlineReady();

  return useQueryAdminSessionsV1({
    retryDelay: SESSIONS_QUERY_RETRY_DELAY,
    enabled:
      isAuthenticated && !!user && isDesktopOnlineReady && sessionQueryVersion === 'v1',
  });
}

export function useQueryTeamMemberSessions() {
  const { teamId, memberId, canReviewOtherUsersSessionList } =
    useSessionListReviewContext();
  const { isAuthenticated } = useIsAuthenticated();
  const isReviewingOtherUser = useIsReviewingOtherUser();
  const sessionQueryVersion = useAtomValue(sessionQueryVersionAtom);
  const isDesktopOnlineReady = useIsDesktopOnlineReady();

  return useQueryTeamMemberSessionsV1({
    teamId,
    memberId,
    fetchTeamMemberSessions: getSessionsByTeamMemberId,
    retryDelay: SESSIONS_QUERY_RETRY_DELAY,
    enabled:
      isAuthenticated &&
      !!canReviewOtherUsersSessionList &&
      !!isReviewingOtherUser &&
      isDesktopOnlineReady &&
      sessionQueryVersion === 'v1',
  });
}
